import { baseApi } from '../../../core/baseApi';
import { createCreateMutation, unwrapApiResponse } from '../../../patterns/crud';
import {
  LoginCredentials,
  LoginResponse,
  LogoutResponse,
  ValidateSessionResponse,
  SessionInfo,
  WsTokenResponse,
  CompleteSetupRequest,
  CompleteSetupResponse,
  CsrfTokenResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  SendOtpRequest,
  SendOtpResponse,
  VerifyOtpRequest,
  VerifyOtpResponse
} from '../types';

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // POST /api/auth/logout
    logout: createCreateMutation<LogoutResponse>('/auth/logout', 'Auth')(builder),

    // POST /api/auth/validate
    validateSession: builder.mutation<ValidateSessionResponse, void>({
      query: () => ({
        url: '/auth/validate',
        method: 'POST'
      }),
      transformResponse: unwrapApiResponse<ValidateSessionResponse>,
      invalidatesTags: ['Auth']
    }),

    // POST /api/auth/session-info
    getSessionInfo: builder.mutation<SessionInfo, void>({
      query: () => ({
        url: '/auth/session-info',
        method: 'POST'
      }),
      transformResponse: unwrapApiResponse<SessionInfo>,
      invalidatesTags: ['Auth']
    }),

    // Removed legacy /api/auth/refresh (use /api/auth/jwt/refresh via middleware/service)

    // POST /api/auth/ws-token
    getWsToken: builder.mutation<WsTokenResponse, void>({
      query: () => ({
        url: '/auth/ws-token',
        method: 'POST'
      }),
      transformResponse: unwrapApiResponse<WsTokenResponse>,
      invalidatesTags: ['Auth']
    }),

    // POST /api/auth/complete-setup
    completeSetup: builder.mutation<CompleteSetupResponse, CompleteSetupRequest>({
      query: (data: CompleteSetupRequest) => ({
        url: '/auth/complete-setup',
        method: 'POST',
        body: data
      }),
      transformResponse: unwrapApiResponse<CompleteSetupResponse>,
      invalidatesTags: ['Auth', 'User', 'Organization']
    }),

    // POST /api/csrf-token
    getCsrfToken: builder.mutation<CsrfTokenResponse, void>({
      query: () => ({
        url: '/csrf-token',
        method: 'POST'
      }),
      transformResponse: unwrapApiResponse<CsrfTokenResponse>,
      invalidatesTags: ['Auth']
    }),

    // POST /api/reset-password
    resetPassword: builder.mutation<ResetPasswordResponse, ResetPasswordRequest>({
      query: (data: ResetPasswordRequest) => ({
        url: '/reset-password',
        method: 'POST',
        body: data
      }),
      transformResponse: unwrapApiResponse<ResetPasswordResponse>,
      invalidatesTags: ['Auth']
    }),

    // POST /api/forgot-password
    forgotPassword: builder.mutation<ForgotPasswordResponse, ForgotPasswordRequest>({
      query: (data: ForgotPasswordRequest) => ({
        url: '/forgot-password',
        method: 'POST',
        body: data
      }),
      transformResponse: unwrapApiResponse<ForgotPasswordResponse>
    }),

    // Additional login endpoint if needed (not in the list but commonly needed)
    login: builder.mutation<LoginResponse, LoginCredentials>({
      query: (credentials: LoginCredentials) => ({
        url: '/auth/login',
        method: 'POST',
        body: credentials
      }),
      transformResponse: unwrapApiResponse<LoginResponse>,
      invalidatesTags: ['Auth', 'User']
    }),

    // POST /api/auth/otp/send - Send OTP for email verification during signup
    sendOtp: builder.mutation<SendOtpResponse, SendOtpRequest>({
      query: (data: SendOtpRequest) => ({
        url: '/auth/otp/send',
        method: 'POST',
        body: data
      }),
      transformResponse: unwrapApiResponse<SendOtpResponse>
    }),

    // POST /api/auth/otp/verify - Verify OTP code during signup
    verifyOtp: builder.mutation<VerifyOtpResponse, VerifyOtpRequest>({
      query: (data: VerifyOtpRequest) => ({
        url: '/auth/otp/verify',
        method: 'POST',
        body: data
      }),
      transformResponse: unwrapApiResponse<VerifyOtpResponse>
    }),
  })
});

// Export hooks
export const {
  useLogoutMutation,
  useValidateSessionMutation,
  useGetSessionInfoMutation,
  useGetWsTokenMutation,
  useCompleteSetupMutation,
  useGetCsrfTokenMutation,
  useResetPasswordMutation,
  useForgotPasswordMutation,
  useLoginMutation,
  useSendOtpMutation,
  useVerifyOtpMutation
} = authApi;